import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, BadgeCheck, Store, SlidersHorizontal } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useMerchantMarket } from '../hooks/useMerchantMarket';
import PriceDisplay from '../components/marketplace/PriceDisplay';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800&q=60';

export default function Products() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const { items, loading, error } = useMerchantMarket('ecommerce', { limit: 300 });
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');
  const [category, setCategory] = useState('all');
  const [brand, setBrand] = useState('all');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [onlyDiscount, setOnlyDiscount] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Derive filter options from the loaded items
  const categories = useMemo(() => Array.from(new Set(items.map(i => i.category).filter(Boolean))).sort(), [items]);
  const brands     = useMemo(() => Array.from(new Set(items.map(i => i.brand).filter(Boolean))).sort(),    [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (category !== 'all') list = list.filter(i => (i.category || '') === category);
    if (brand !== 'all')    list = list.filter(i => (i.brand || '') === brand);
    if (onlyDiscount)       list = list.filter(i => i.hasDiscount);
    const pMin = parseFloat(priceMin);
    const pMax = parseFloat(priceMax);
    if (!Number.isNaN(pMin)) list = list.filter(i => !i.priceOnRequest && ((i.salePrice||i.price)||0) >= pMin);
    if (!Number.isNaN(pMax)) list = list.filter(i => !i.priceOnRequest && ((i.salePrice||i.price)||0) <= pMax);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(i =>
        (i.name || '').toLowerCase().includes(s) ||
        (i.nameEn || '').toLowerCase().includes(s) ||
        (i.brand || '').toLowerCase().includes(s) ||
        (i.category || '').toLowerCase().includes(s) ||
        (i.sku || '').toLowerCase().includes(s) ||
        (i.store?.name || '').toLowerCase().includes(s),
      );
    }
    if (sort === 'price_asc')       list = [...list].filter(i => !i.priceOnRequest).sort((a,b) => ((a.salePrice||a.price)||0) - ((b.salePrice||b.price)||0));
    else if (sort === 'price_desc') list = [...list].filter(i => !i.priceOnRequest).sort((a,b) => ((b.salePrice||b.price)||0) - ((a.salePrice||a.price)||0));
    else                             list = [...list].sort((a,b) => (b.created_at||'').localeCompare(a.created_at||''));
    return list;
  }, [items, q, sort, category, brand, priceMin, priceMax, onlyDiscount]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero — premium gradient with decorative shapes */}
      <section className={`relative overflow-hidden py-16 ${isDarkMode ? 'bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]' : 'bg-gradient-to-br from-[#7C3AED] via-[#6366F1] to-[#1E9BD9]'}`}>
        <div className="absolute inset-0">
          <div className="absolute top-0 end-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 start-1/4 w-72 h-72 bg-[#F59E0B]/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-4 border border-white/20">
            <Store className="h-3.5 w-3.5" />
            {isAr ? 'منتجات من متاجر معتمدة وموثقة' : 'Products from certified & verified stores'}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white tracking-tight" data-testid="page-title">
            {isAr ? 'المتجر' : 'The Store'}
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
            {isAr ? 'تسوّق أفضل المنتجات من متاجر موثوقة عبر الإمارات' : 'Shop the best products from trusted stores across the UAE'}
          </p>
        </div>
      </section>

      {/* Filters — main row + advanced */}
      <section className="max-w-6xl mx-auto px-4 py-6">
        <div className={`rounded-2xl p-4 space-y-3 ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-[#E2E8F0] shadow-sm'}`}>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e)=>setQ(e.target.value)}
                placeholder={isAr ? 'اسم المنتج، الماركة، الفئة…' : 'Product name, brand, category…'}
                className="h-11 ps-9 rounded-xl" data-testid="products-search" />
            </div>
            <select value={category} onChange={(e)=>setCategory(e.target.value)}
              className={`h-11 rounded-xl px-3 border text-sm ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : 'bg-white border-[#E2E8F0]'}`}
              data-testid="products-category">
              <option value="all">{isAr ? 'كل الفئات' : 'All categories'}</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={sort} onChange={(e)=>setSort(e.target.value)}
              className={`h-11 rounded-xl px-3 border text-sm ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : 'bg-white border-[#E2E8F0]'}`}
              data-testid="products-sort">
              <option value="newest">{isAr ? 'الأحدث' : 'Newest'}</option>
              <option value="price_asc">{isAr ? 'السعر: منخفض → مرتفع' : 'Price: Low → High'}</option>
              <option value="price_desc">{isAr ? 'السعر: مرتفع → منخفض' : 'Price: High → Low'}</option>
            </select>
            <button
              onClick={()=>setShowAdvanced(v => !v)}
              className={`h-11 rounded-xl px-4 text-sm font-semibold flex items-center gap-2 border ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white hover:border-[#1E9BD9]/50' : 'bg-white border-[#E2E8F0] text-slate-700 hover:border-[#1E9BD9]/50'}`}
              data-testid="products-advanced-toggle"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {isAr ? 'خيارات أكثر' : 'More filters'}
            </button>
          </div>
          {showAdvanced && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-slate-200/50 dark:border-white/5">
              <select value={brand} onChange={(e)=>setBrand(e.target.value)}
                className={`h-11 rounded-xl px-3 border text-sm ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : 'bg-white border-[#E2E8F0]'}`}
                data-testid="products-brand">
                <option value="all">{isAr ? 'كل الماركات' : 'All brands'}</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <Input value={priceMin} onChange={(e)=>setPriceMin(e.target.value)}
                placeholder={isAr ? 'السعر من (AED)' : 'Min price (AED)'}
                className="h-11 rounded-xl" type="number" data-testid="products-price-min" />
              <Input value={priceMax} onChange={(e)=>setPriceMax(e.target.value)}
                placeholder={isAr ? 'السعر إلى (AED)' : 'Max price (AED)'}
                className="h-11 rounded-xl" type="number" data-testid="products-price-max" />
              <label className={`inline-flex items-center gap-2 h-11 rounded-xl px-3 border text-sm cursor-pointer ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : 'bg-white border-[#E2E8F0] text-slate-700'}`}>
                <input type="checkbox" checked={onlyDiscount} onChange={(e)=>setOnlyDiscount(e.target.checked)}
                  data-testid="products-only-discount" className="accent-[#1E9BD9]" />
                {isAr ? 'الخصومات فقط' : 'Discounts only'}
              </label>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>
        ) : error ? (
          <p className="text-center text-red-500 py-24">{error}</p>
        ) : filtered.length === 0 ? (
          <p className="text-center py-24 text-slate-400">{isAr ? 'لا توجد منتجات متاحة' : 'No products available'}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map(it => {
              const imgSrc = (Array.isArray(it.images) && it.images[0]) || it.image || PLACEHOLDER;
              const name = (isAr ? it.name : (it.nameEn || it.name)) || it.title || it.sku || (isAr ? 'منتج' : 'Product');
              return (
              <Link key={it.id} to={`/product/${it.id}`} state={{ store: it.store, item: it }}
                className={`group rounded-2xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                  isDarkMode ? 'bg-[#141414] border border-white/5 hover:border-[#1E9BD9]/40' : 'bg-white border border-[#E2E8F0] hover:border-[#1E9BD9]/40 shadow-sm'
                }`}
                data-testid={`product-card-${it.id}`}
              >
                <div className="aspect-square overflow-hidden bg-slate-100 dark:bg-white/5">
                  <img src={imgSrc}
                    alt={name}
                    onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-sm font-semibold text-[#0F172A] dark:text-white line-clamp-2 flex-1">
                      {name}
                    </h3>
                    {it.store?.verified && <BadgeCheck className="h-3.5 w-3.5 text-[#1E9BD9] shrink-0" />}
                  </div>
                  <PriceDisplay item={it} locale={isAr ? 'ar' : 'en'} size="sm" />
                  <p className="text-xs text-slate-400 truncate mt-1">{it.store?.name || '—'}</p>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
